/* ==========================================================================
   FIGS bag — the cart drawer every prototype shares, and the add-to-bag
   choreography that feeds it.

   Source: the Navigation artifact's cart drawer (Figma Cart section
   1749:18162 · Single Item 1776:102635 desktop / 1776:102541 mobile),
   namespaced `fnav-cart-` like the rest of the nav. The artifact built the
   Single Item state only. Empty State (1749:18163 / 1749:18208) and
   Multi-Unique Item (1776:120369 / 1776:120412) come from the same Figma
   section, so the drawer shows whatever a prototype actually adds.

   mountFigsNav() mounts it, so every page with the nav has the bag. Pages
   only add to it:

     import { addToFigsBag } from '/src/figs-nav/figs-nav.js';
     addToFigsBag({ id, name, color, size, type, price, image }, { from: img });
     addToFigsBag([top, pant], { open: false }); // a drawer's add: it opens
     openFigsBag();                              // the bag once it has gone

   The bag is one list in localStorage, so it follows a reviewer from the PDP
   to a PLP to the Homepage, and between tabs.

   What's being decided: the 06 cart-drawer sequence on real pages. From the
   page, the product flies from where it was picked to the header bag, the
   badge pops as it lands, and the drawer opens on the line that just
   arrived. From a drawer (Quick Buy, a set's sizes) the drawer confirms and
   leaves first, then the bag opens — two panels never stack. Quantity flips
   like an odometer; Remove slides the line out and closes the gap.

   Reduced motion: no flight, no badge pop, the drawer crossfades instead of
   sliding, and lines and quantities change in place.

   Embroidery (Figma Embroidered Item 2433:25226): a line added with an
   `embroidery` payload (the shared configurator's bagLine(): the charges,
   the garment, the placements) is FINAL SALE, carries an EMBROIDERY row
   — its total, EDIT, a chevron that opens the charges with REMOVE on each
   — and, while open, its thumbnail turns to the embroidered garment. EDIT
   reopens the configurator on that line (editExternal); the bag's foot
   gains the final-sale / two-week banner; the subtotal counts the charges.
   The configurator is loaded only when the bag needs it.
   ========================================================================== */

import { animate, stagger } from 'https://cdn.jsdelivr.net/npm/motion@12.23.12/+esm';
import { setParts, onModelOf } from './figs-sets.js';
import './figs-cart.css';

const STORE = 'figs-lab:bag';
const FREE_SHIPPING_AT = 50; // the promo bar's "Free Shipping & Returns on Orders $50+"
const EMPTY_IMAGE = new URL('./assets/cart-empty.jpg', import.meta.url).href;
const SPOOL_ICON = new URL('./assets/cart-embroidery-spool.svg', import.meta.url).href;
const CHEVRON_SVG = '<svg class="fnav-cart-emb-chevron" width="11.0254" height="6" viewBox="0 0 11.0254 6" fill="none" aria-hidden="true"><path d="M11.0254 0.487305L5.5127 6L0 0.487305L0.487305 0L5.5127 5.02539L10.5381 0L11.0254 0.487305Z" fill="currentColor"/></svg>';
/* the shared embroidery configurator — loaded the first time the bag draws
   an embroidered garment or a shopper edits one */
let embModule = null;
const loadEmbroidery = () => (embModule ||= import('../figs-embroidery/figs-embroidery.js'));
/* the empty state's two links land on lab prototypes, not wearfigs.com */
const EMPTY_LINKS = [
  ['Shop New Arrivals', '/experiments/14-plp-scrub-sets/'],
  ['Shop Best Sellers', '/experiments/03-plp-prototype/'],
];
/* the nav's own drawers open and close on these (figs-nav.js) */
const DRAWER_EASE = [0.4, 0, 0.2, 1];

/* motion.css tokens where the page loads them (PDP, Uniform Builder), the
   file's own values where it doesn't (PLPs, Homepage). Read per use, so a
   timing tuned in the Vocabulary playground applies here too. */
function mo() {
  const cs = getComputedStyle(document.documentElement);
  const sec = (name, fallback) => {
    const v = cs.getPropertyValue(name).trim();
    if (!v) return fallback;
    return v.endsWith('ms') ? parseFloat(v) / 1000 : parseFloat(v);
  };
  const ease = (name, fallback) => {
    const m = cs.getPropertyValue(name).match(/-?[\d.]+/g);
    return m && m.length === 4 ? m.map(Number) : fallback;
  };
  return {
    quick: sec('--mo-quick', 0.2),
    base: sec('--mo-base', 0.32),
    slow: sec('--mo-slow', 0.56),
    stagTight: sec('--mo-stagger-tight', 0.04),
    eStandard: ease('--mo-ease-standard', [0.4, 0, 0.2, 1]),
    eEntrance: ease('--mo-ease-entrance', [0.16, 1, 0.3, 1]),
    eExit: ease('--mo-ease-exit', [0.7, 0, 0.84, 0]),
  };
}

/* the OS setting, or the lab bar's Reduced toggle (the PDP and Uniform
   Builder mirror it onto <html data-reduced>) */
const isReduced = () =>
  matchMedia('(prefers-reduced-motion: reduce)').matches ||
  document.documentElement.dataset.reduced === 'true';

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
/* ™ is Roboto Mono, like every superscript */
const tm = (s) => esc(s).replace(/™/g, '<span class="fnav-cart-tm">™</span>');
const money = (n) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const absolute = (src) => {
  if (!src || /^(blob|data):/.test(src)) return src || '';
  try { return new URL(src, location.href).href; } catch { return src; }
};
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/* ---------------------------------------------------------------------------
   The bag — newest line first, so what was just added is what the drawer
   opens on.
   ------------------------------------------------------------------------ */
let migrated = false;
let lines = load();
if (migrated) save();
const unseen = new Set(); // keys added since the drawer last showed them

function load() {
  try {
    const v = JSON.parse(localStorage.getItem(STORE) || '[]');
    const saved = Array.isArray(v) ? v.filter((l) => l && l.key && l.name && l.qty > 0) : [];
    /* a bag saved before sets split (one "Rafaela Set" line) opens as its
       garments, and a line saved with a ghost opens on model */
    const out = [];
    saved.forEach((entry) => {
      let l = entry;
      if (l.ghost) {
        l = normalize({ ...l, ...onModelOf(l) }); // normalize drops the flag
        migrated = true;
      }
      const parts = setParts(l);
      if (parts) migrated = true;
      (parts ? parts.map(normalize) : [l]).forEach((line) => {
        const hit = out.find((o) => o.key === line.key);
        if (hit) hit.qty += line.qty; else out.push(line);
      });
    });
    return out;
  } catch {
    return [];
  }
}
function save() {
  try { localStorage.setItem(STORE, JSON.stringify(lines)); } catch { /* private mode: the bag lasts the page */ }
}
const count = () => lines.reduce((n, l) => n + l.qty, 0);
const embTotal = (l) => (l.embroidery ? Number(l.embroidery.total) || 0 : 0);
const subtotal = () => lines.reduce((n, l) => n + l.qty * (l.price + embTotal(l)), 0);

/* A set is never one line: an item with `parts`, or one named as a set
   (figs-sets.js), becomes one line per garment. */
function explode(item) {
  if (Array.isArray(item.parts) && item.parts.length) {
    return item.parts.map((p) => ({ color: item.color, qty: item.qty, ...p }));
  }
  return setParts(item) || [item];
}

/* One line per product × colour × size × length (× embroidery). Adding the
   same again raises its quantity rather than repeating it. The thumbnail is
   always the on-model photo; `fallback` is another on-model shot for when
   the colour's photo is missing. */
function normalize(item) {
  const line = {
    id: item.id || slug(item.name),
    name: String(item.name || 'Item'),
    color: item.color || '',
    size: item.size || '',
    type: item.type && item.type !== 'Regular' ? item.type : '',
    note: item.note || '',
    embroidery: validEmbroidery(item.embroidery),
    price: Number(item.price) || 0,
    image: absolute(item.image),
    fallback: item.fallback ? absolute(item.fallback) : '',
    url: item.url ? absolute(item.url) : '',
    qty: Math.max(1, Math.floor(Number(item.qty) || 1)),
  };
  line.key = keyOf(line);
  return line;
}

/* the same garment embroidered differently is a different line */
function keyOf(line) {
  return [line.id, line.color, line.size, line.type, line.note].map(slug).join('|')
    + (line.embroidery ? `|emb-${hash(JSON.stringify(line.embroidery.placements))}` : '');
}
function hash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
const validEmbroidery = (e) => (e && Number(e.total) > 0 && Array.isArray(e.items) && e.items.length
  && Array.isArray(e.placements) ? e : null);

/* A recoloured image (the Uniform Builder's) is a blob: URL that dies with
   the page; the bag keeps a small copy instead, at twice the 120×160 slot. */
async function keepable(src) {
  if (!src || !src.startsWith('blob:')) return src;
  const img = new Image();
  img.src = src;
  await img.decode();
  const scale = Math.min(1, 320 / img.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/webp', 0.85);
}

/* ---------------------------------------------------------------------------
   Bag icons — the masthead's, the compact bar's and the mobile drawer's.
   Each becomes a button that opens the bag and carries the count badge.
   ------------------------------------------------------------------------ */
const BAG_ICONS = ['.fnav-masthead-icons', '.fnav-compact-bar-icons', '.fnav-mobile-nav-icons']
  .map((group) => `${group} svg[aria-label^="Bag"]`)
  .join(', ');

function wrapBagIcons() {
  document.querySelectorAll(BAG_ICONS).forEach((svg) => {
    if (svg.parentElement.classList.contains('fnav-bag')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fnav-bag';
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.setAttribute('aria-controls', 'fnav-cart-drawer');
    svg.replaceWith(btn);
    svg.removeAttribute('aria-label');
    svg.setAttribute('aria-hidden', 'true');
    const badge = document.createElement('span');
    badge.className = 'fnav-bag__count';
    badge.setAttribute('aria-hidden', 'true');
    badge.dataset.empty = 'true';
    btn.append(svg, badge);
  });
  return [...document.querySelectorAll('.fnav-bag')];
}

/* The badge reads the bag's count. The bump follows the 06 cart-drawer
   decision: the badge pops on a spring and the bag itself nudges, because a
   badge alone is easy to miss. */
export function setFigsNavBagCount(n, { bump = true } = {}) {
  n = Math.max(0, Math.floor(Number(n)) || 0);
  const label = `Bag, ${n} ${n === 1 ? 'item' : 'items'}`;
  const pop = bump && n > 0 && !isReduced();
  wrapBagIcons().forEach((btn) => {
    const badge = btn.querySelector('.fnav-bag__count');
    const svg = btn.querySelector('svg');
    const wasEmpty = badge.dataset.empty === 'true';
    badge.textContent = String(n);
    badge.dataset.empty = String(n === 0);
    btn.setAttribute('aria-label', label);
    if (!pop) return;
    animate(badge, { scale: wasEmpty ? [0.4, 1] : [1.35, 1] }, { type: 'spring', stiffness: 520, damping: 14 });
    animate(svg, { rotate: [-8, 0] }, { type: 'spring', stiffness: 420, damping: 10 });
  });
}

function visibleBag() {
  return [...document.querySelectorAll('.fnav-bag')].find((b) => {
    const r = b.getBoundingClientRect();
    return r.width && r.bottom > 0 && r.top < innerHeight && getComputedStyle(b).visibility !== 'hidden';
  });
}

/* ---------------------------------------------------------------------------
   Fly-to-bag — the 06 cue, as the PDP's Wear It With card first shipped it:
   a copy of the product image leaves where it was picked and lands on the
   header bag, small. `from` may be a list; the first one on screen wins. An
   image is cloned; anything else (a sticky ATB) launches `image` from it.
   Resolves as the copy reaches the bag, which is when the badge should pop.
   ------------------------------------------------------------------------ */
const FLY_MS = 700;
const LAND_MS = 560;

export function flyToFigsBag(from, image) {
  const bag = visibleBag();
  const onScreen = (el) => {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
  };
  const origin = [].concat(from).find(onScreen);
  if (isReduced() || !bag || !origin) return Promise.resolve();

  /* what flies is the bag line's photo — on model — from where the product
     was picked. A pick made from a ghost (Wear It With, the Uniform Builder's
     figure) flies its on-model photo in the ghost's box, never the ghost. */
  const r = origin.getBoundingClientRect();
  let flyer;
  let box;
  if (origin.tagName === 'IMG' && (!image || absolute(image) === (origin.currentSrc || origin.src))) {
    flyer = origin.cloneNode();
    flyer.removeAttribute('class');
    flyer.removeAttribute('loading');
    box = r;
    flyer.style.objectFit = getComputedStyle(origin).objectFit || 'cover';
  } else {
    if (!image) return Promise.resolve();
    flyer = new Image();
    flyer.src = image;
    flyer.style.objectFit = 'cover';
    const w = origin.tagName === 'IMG' ? Math.min(r.width, r.height * 0.75) : 72;
    const h = w * 4 / 3;
    box = { left: r.left + r.width / 2 - w / 2, top: r.top + r.height / 2 - h / 2, width: w, height: h };
  }
  flyer.alt = '';
  flyer.className = 'fnav-cart-fly';
  Object.assign(flyer.style, { left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` });
  document.body.append(flyer);

  const to = bag.getBoundingClientRect();
  const dx = to.left + to.width / 2 - (box.left + box.width / 2);
  const dy = to.top + to.height / 2 - (box.top + box.height / 2);
  const scale = Math.max(0.08, Math.min(0.5, 40 / Math.max(box.width, box.height)));
  animate(flyer, { x: [0, dx], y: [0, dy], scale: [1, scale], opacity: [1, 1, 0.2] },
    { duration: FLY_MS / 1000, ease: [0.5, 0, 0.75, 0], opacity: { duration: FLY_MS / 1000, times: [0, 0.7, 1] } })
    .finished.then(() => flyer.remove(), () => flyer.remove());
  return wait(LAND_MS);
}

/* ---------------------------------------------------------------------------
   The drawer
   ------------------------------------------------------------------------ */
const CLOSE_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12.0479 11.1279L18.96 4.21582L19.6621 4.91895L12.751 11.8301L20 19.0791L19.2949 19.7842L12.0459 12.5352L4.70312 19.8809L4 19.1777L11.3438 11.833L4.33398 4.82324L5.03906 4.11914L12.0479 11.1279Z" fill="currentColor"/></svg>';
const MINUS_SVG = '<svg width="8" height="2" viewBox="0 0 8 2" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M0 0.0231047L3.29796 0.034657L4.6966 0C6.47619 0.127076 7.76054 0.589169 8 1.6L4.68027 1.58267L3.26531 1.55957C1.50204 1.51913 0.239456 1.01661 0 0.0231047Z" fill="currentColor"/></svg>';
const PLUS_SVG = '<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M4.70014 0.0217096L4.68386 3.22388C6.45862 3.34328 7.73949 3.77748 7.97829 4.72727L4.66757 4.71099L4.65129 8L3.27273 7.97829L3.25645 4.68928C1.49797 4.65129 0.238806 4.1791 0 3.24559L3.28901 3.25645L3.30529 0L4.70014 0.0217096Z" fill="currentColor"/></svg>';

const DRAWER_HTML = `
<div class="fnav-cart-drawer-scrim" id="fnav-cart-drawer-scrim"></div>
<div class="fnav-cart-drawer" id="fnav-cart-drawer" role="dialog" aria-modal="true" aria-label="Bag" aria-hidden="true" inert>
  <div class="fnav-cart-drawer-top">
    <div class="fnav-cart-drawer-row">
      <h2 class="fnav-cart-drawer-title" id="fnav-cart-drawer-title">bag</h2>
      <button class="fnav-cart-drawer-close" id="fnav-cart-drawer-close" type="button" aria-label="Close bag">${CLOSE_SVG}</button>
    </div>
  </div>

  <div class="fnav-cart-empty" data-cart-empty>
    <div class="fnav-cart-empty-copy">
      <p class="fnav-cart-empty-headline">
        <span class="fnav-cart-empty-line">Your bag is empty.</span>
        <span class="fnav-cart-empty-line">Not sure where to start?</span>
      </p>
      <div class="fnav-cart-empty-links">
        ${EMPTY_LINKS.map(([label, href]) => `<a class="fnav-cart-empty-link" href="${href}">${label}</a>`).join('')}
      </div>
    </div>
    <img class="fnav-cart-empty-image" src="${EMPTY_IMAGE}" alt="" loading="lazy" decoding="async">
  </div>

  <div class="fnav-cart-drawer-scroll" data-cart-scroll>
    <ul class="fnav-cart-drawer-products" data-cart-lines aria-label="Items in your bag"></ul>

    <!-- Embroidery Banner (2439:26684): while anything in the bag is
         embroidered — after the lines, pushed down to sit over the foot
         when they're short (the closed frame), following them when long -->
    <div class="fnav-cart-emb-banner" data-cart-emb-banner hidden>
      <img class="fnav-cart-emb-banner-icon" src="${SPOOL_ICON}" alt="" width="8.533" height="16.96">
      <p class="fnav-cart-emb-banner-copy">Embroidered items are final sale and take up to 2 weeks to ship. This also includes orders with Express Shipping.</p>
    </div>
  </div>

  <div class="fnav-cart-drawer-bottom" data-cart-bottom>
    <div class="fnav-cart-drawer-copy">
      <div class="fnav-cart-subtotal-row">
        <span class="fnav-cart-subtotal-label">Subtotal</span>
        <span class="fnav-cart-subtotal-value" data-cart-subtotal></span>
      </div>
      <p class="fnav-cart-shipping-note" data-cart-shipping></p>
    </div>
    <button class="fnav-cart-checkout-btn" type="button">Proceed to Checkout</button>
  </div>
</div>`;

const openEmb = new Set(); // embroidered lines whose charges are showing

/* Cart Embroidery Option (2433:25647): an 80 square in the garment's colour
   holding the logo (56), the icon (40) or the text (Body/FIGS/Tiny in the
   thread colour), then "Logo—$60", the detail and the placement, REMOVE */
function embItemHTML(it, hex) {
  const thumb = it.part === 'text'
    ? `<span class="fnav-cart-emb-thumb-text" style="color:${esc(it.thread || '#fff')};font-family:${esc(it.font || 'inherit')}${it.weight ? `;font-weight:${esc(it.weight)}` : ''}${it.smallCaps ? ';font-variant-caps:all-small-caps' : ''}">${(it.text || []).map(esc).join('<br>')}</span>`
    : `<img class="fnav-cart-emb-art fnav-cart-emb-art--${it.part}" src="${esc(it.art || '')}" alt="">`;
  return `
      <li class="fnav-cart-emb-item" data-cart-emb-item="${esc(`${it.place}:${it.part}`)}">
        <span class="fnav-cart-emb-thumb" style="background:${esc(hex)}" aria-hidden="true">${thumb}</span>
        <span class="fnav-cart-emb-body">
          <span class="fnav-cart-emb-text">
            <span class="fnav-cart-emb-name">${esc(it.title)}—<span class="fnav-cart-emb-cost">${money(it.price)}</span></span>
            ${it.details.map((d) => `<span class="fnav-cart-emb-detail">${esc(d)}</span>`).join('')}
          </span>
          <button class="fnav-cart-emb-remove" type="button" data-cart-emb-remove="${esc(`${it.place}:${it.part}`)}">Remove<span class="fnav-cart-sr"> ${esc(it.title.toLowerCase())}</span></button>
        </span>
      </li>`;
}

function lineHTML(l) {
  const size = [l.size, l.type].filter(Boolean).join(' ');
  const fallback = l.fallback ? ` onerror="this.onerror=null;this.src='${esc(l.fallback)}'"` : '';
  const emb = l.embroidery;
  const isOpen = emb && openEmb.has(l.key);
  /* while the charges show, the thumbnail is the embroidered garment */
  const preview = emb ? '<span class="fnav-cart-emb-preview" data-cart-emb-preview aria-hidden="true"></span>' : '';
  const media = `<img src="${esc(l.image)}" alt="" decoding="async"${fallback}>${preview}`;
  const image = l.url
    ? `<a class="fnav-cart-product-image" href="${esc(l.url)}" tabindex="-1" aria-hidden="true">${media}</a>`
    : `<span class="fnav-cart-product-image">${media}</span>`;
  const plain = l.name.replace(/™/g, '');
  const id = `fnav-cart-emb-${hash(l.key)}`;
  const row = `
    <div class="fnav-cart-product-card">
      ${image}
      <div class="fnav-cart-product-middle">
        <div class="fnav-cart-product-info">
          <span class="fnav-cart-product-name">${tm(l.name)}</span>
          <span class="fnav-cart-product-variant">${size ? `<span class="fnav-cart-size-label">${esc(size)}—</span>` : ''}<span class="fnav-cart-color-label">${esc(l.color)}</span></span>
          ${l.note ? `<span class="fnav-cart-product-note">${esc(l.note)}</span>` : ''}
          ${emb ? '<span class="fnav-cart-final-sale">Final sale</span>' : ''}
        </div>
        <div class="fnav-cart-product-actions">
          <div class="fnav-cart-qty-selector" role="group" aria-label="Quantity, ${esc(plain)}">
            <button class="fnav-cart-qty-btn" type="button" data-cart-qty="-1" aria-label="Decrease quantity"${l.qty <= 1 ? ' aria-disabled="true"' : ''}>${MINUS_SVG}</button>
            <span class="fnav-cart-qty-value" aria-live="polite"><span class="fnav-cart-qty-digit" data-cart-digit>${l.qty}</span></span>
            <button class="fnav-cart-qty-btn" type="button" data-cart-qty="1" aria-label="Increase quantity">${PLUS_SVG}</button>
          </div>
          <button class="fnav-cart-remove-link" type="button" data-cart-remove aria-label="Remove ${esc(plain)}">Remove</button>
        </div>
      </div>
    </div>
    <span class="fnav-cart-product-price" data-cart-price>${money(l.qty * l.price)}</span>`;
  if (!emb) {
    return `
  <li class="fnav-cart-drawer-content" data-cart-line="${esc(l.key)}">${row}
  </li>`;
  }
  /* Embroidery Accordion (2433:25752): chevron + EMBROIDERY + EDIT on the
     left, the line's embroidery total on the right; the charges below */
  return `
  <li class="fnav-cart-drawer-content fnav-cart-drawer-content--emb" data-cart-line="${esc(l.key)}"${isOpen ? ' data-emb-open' : ''}>
    <div class="fnav-cart-line-main">${row}
    </div>
    <div class="fnav-cart-emb">
      <div class="fnav-cart-emb-row">
        <span class="fnav-cart-emb-left">
          <button class="fnav-cart-emb-toggle" type="button" aria-expanded="${isOpen}" aria-controls="${id}" data-cart-emb-toggle>${CHEVRON_SVG}<span class="fnav-cart-emb-label">Embroidery</span></button>
          <button class="fnav-cart-emb-edit" type="button" aria-haspopup="dialog" data-cart-emb-edit>Edit<span class="fnav-cart-sr"> embroidery, ${esc(plain)}</span></button>
        </span>
        <span class="fnav-cart-emb-price" data-cart-emb-price>${money(l.qty * emb.total)}</span>
      </div>
      <ul class="fnav-cart-emb-items" id="${id}"${isOpen ? '' : ' hidden'}>${emb.items.map((it) => embItemHTML(it, emb.garment?.hex || '#2b2e43')).join('')}
      </ul>
    </div>
  </li>`;
}

const shippingNote = (total) => (total >= FREE_SHIPPING_AT
  ? 'You’ve qualified for free shipping'
  : `Spend another ${money(FREE_SHIPPING_AT - total)} to qualify for free shipping`);

let ui = null; // the mounted drawer

function render() {
  if (!ui) return;
  const empty = lines.length === 0;
  ui.drawer.classList.toggle('fnav-cart-drawer--empty', empty);
  ui.title.hidden = empty;
  ui.empty.hidden = !empty;
  ui.list.hidden = empty;
  ui.scroll.hidden = empty;
  ui.bottom.hidden = empty;
  ui.list.innerHTML = lines.map(lineHTML).join('');
  ui.embBanner.hidden = empty || !lines.some((l) => l.embroidery);
  lines.filter((l) => l.embroidery && openEmb.has(l.key)).forEach((l) => drawPreview(l));
  syncTotals();
}

/* the embroidered garment in the thumbnail slot: the stage at 110 of the
   120 slot on Background/Subtle, as the open frame draws it */
async function drawPreview(l) {
  const host = lineEl(l.key)?.querySelector('[data-cart-emb-preview]');
  if (!host || host.dataset.drawn === l.key) return;
  host.dataset.drawn = l.key;
  try {
    const mod = await loadEmbroidery();
    mod.renderEmbroideryPreview(host, { garment: l.embroidery.garment, placements: l.embroidery.placements, fit: 110 / 120 });
  } catch (err) { console.error('[bag] embroidery preview', err); }
}

/* open / close a line's charges: the list's height moves on the standard
   curve with its start pinned (motion's first height frame lands a frame
   late), the rows rise in on the tight stagger, the thumbnail crossfades */
function toggleEmb(li, line, open) {
  const list = li.querySelector('.fnav-cart-emb-items');
  const btn = li.querySelector('[data-cart-emb-toggle]');
  btn.setAttribute('aria-expanded', String(open));
  li.toggleAttribute('data-emb-open', open);
  if (open) openEmb.add(line.key); else openEmb.delete(line.key);
  if (open) drawPreview(line);
  const gap = 16;
  if (isReduced()) { list.hidden = !open; return; }
  const t = mo();
  const from = open ? 0 : list.getBoundingClientRect().height;
  list.hidden = false;
  const to = open ? list.getBoundingClientRect().height : 0;
  list.style.overflow = 'clip';
  list.style.height = `${from}px`;
  list.style.marginTop = open ? `${-gap}px` : '0px';
  const rows = [...list.children];
  if (open) {
    animate(rows, { opacity: [0, 1], y: [8, 0] }, { duration: t.slow, ease: t.eEntrance, delay: stagger(t.stagTight, { startDelay: 0.08 }) });
  } else {
    animate(rows, { opacity: [1, 0] }, { duration: t.quick, ease: t.eStandard });
  }
  animate(list, { height: [from, to], marginTop: open ? [-gap, 0] : [0, -gap] }, { duration: open ? t.slow : t.base, ease: t.eStandard })
    .finished.then(() => requestAnimationFrame(() => {
      if (li.hasAttribute('data-emb-open') !== open) return; // a newer tap owns it
      if (!open) list.hidden = true;
      Object.assign(list.style, { height: '', marginTop: '', overflow: '' });
      rows.forEach((r) => { r.style.opacity = ''; r.style.transform = ''; });
    }), () => {});
}

/* the line's embroidery changed (EDIT's DONE, a REMOVE): re-key it — the same
   garment differently embroidered is another line, and one that now matches
   an existing line joins it — and redraw */
function setLineEmbroidery(line, payload) {
  const wasOpen = openEmb.has(line.key);
  openEmb.delete(line.key);
  line.embroidery = validEmbroidery(payload);
  line.key = keyOf(line);
  const twin = lines.find((o) => o !== line && o.key === line.key);
  if (twin) { twin.qty += line.qty; lines = lines.filter((o) => o !== line); }
  if (wasOpen && line.embroidery) openEmb.add((twin || line).key);
  save();
  render();
  setFigsNavBagCount(count(), { bump: false });
}

async function editEmb(btn, line) {
  try {
    const mod = await loadEmbroidery();
    const emb = mod.createFigsEmbroidery({ garment: line.embroidery.garment });
    emb.editExternal({
      garment: line.embroidery.garment,
      placements: line.embroidery.placements,
      trigger: btn,
      onDone: (payload) => setLineEmbroidery(line, payload),
    });
  } catch (err) { console.error('[bag] embroidery edit', err); }
}

/* REMOVE one charge: the row slides out and its gap closes; the last one
   folds the whole Embroidery row away before the line turns plain */
async function removeEmbItem(li, line, place, part) {
  const row = li.querySelector(`[data-cart-emb-item="${CSS.escape(`${place}:${part}`)}"]`);
  const last = line.embroidery.items.length === 1;
  if (row && !isReduced()) {
    const t = mo();
    await animate(row, { opacity: [1, 0], x: [0, 24] }, { duration: t.base, ease: t.eStandard }).finished;
    const box = last ? li.querySelector('.fnav-cart-emb') : row;
    const h = box.getBoundingClientRect().height;
    box.style.overflow = 'clip';
    box.style.height = `${h}px`;
    await animate(box, { height: [h, 0], marginTop: [0, last ? -16 : -12], opacity: last ? [1, 0] : 0 },
      { duration: t.base, ease: t.eStandard }).finished;
  }
  const mod = await loadEmbroidery();
  setLineEmbroidery(line, mod.embroideryWithout(line.embroidery, place, part));
}

function syncTotals() {
  const total = subtotal();
  ui.subtotal.textContent = money(total);
  ui.shipping.textContent = shippingNote(total);
}

const lineEl = (key) => ui.list.querySelector(`[data-cart-line="${CSS.escape(key)}"]`);

/* lines added while the drawer was shut rise in as it opens */
function riseUnseen({ delay = 0 } = {}) {
  const els = [...unseen].map(lineEl).filter(Boolean);
  unseen.clear();
  if (!els.length) return;
  ui.scroll.scrollTop = 0; // new lines are first
  if (isReduced()) return;
  const t = mo();
  animate(els, { opacity: [0, 1], y: [16, 0] }, { duration: t.slow, ease: t.eEntrance, delay: stagger(t.stagTight, { startDelay: delay }) });
}

/* the odometer: the old number leaves the way the count moved, the new one
   arrives from the other side */
async function flipQty(li, value, dir) {
  const digit = li.querySelector('[data-cart-digit]');
  const price = li.querySelector('[data-cart-price]');
  const line = lines.find((l) => l.key === li.dataset.cartLine);
  const minus = li.querySelector('[data-cart-qty="-1"]');
  if (value <= 1) minus.setAttribute('aria-disabled', 'true');
  else minus.removeAttribute('aria-disabled');
  const embPrice = li.querySelector('[data-cart-emb-price]');
  if (isReduced()) {
    digit.textContent = String(value);
    price.textContent = money(value * line.price);
    if (embPrice) embPrice.textContent = money(value * embTotal(line));
    return;
  }
  const t = mo();
  animate([price, embPrice].filter(Boolean), { opacity: [1, 0.35, 1] }, { duration: t.quick + t.base, ease: t.eStandard });
  setTimeout(() => {
    price.textContent = money(value * line.price);
    if (embPrice) embPrice.textContent = money(value * embTotal(line));
  }, t.quick * 1000);
  await animate(digit, { y: ['0%', dir > 0 ? '-80%' : '80%'], opacity: [1, 0] }, { duration: t.quick, ease: t.eExit }).finished;
  digit.textContent = String(value);
  animate(digit, { y: [dir > 0 ? '80%' : '-80%', '0%'], opacity: [0, 1] }, { duration: t.base, ease: t.eEntrance });
}

async function removeLine(li) {
  const key = li.dataset.cartLine;
  if (li.dataset.removing) return;
  li.dataset.removing = 'true';
  if (!isReduced()) {
    const t = mo();
    li.style.overflow = 'hidden';
    animate(li, { opacity: 0, x: 40 }, { duration: t.base, ease: t.eExit });
    await animate(li, { height: [`${li.offsetHeight}px`, '0px'], marginBottom: '0px' },
      { duration: t.slow, ease: t.eStandard, delay: t.quick }).finished;
  }
  lines = lines.filter((l) => l.key !== key);
  save();
  setFigsNavBagCount(count(), { bump: false });
  const wasLast = lines.length === 0;
  render();
  if (wasLast && !isReduced()) {
    const t = mo();
    animate(ui.empty, { opacity: [0, 1] }, { duration: t.base, ease: t.eEntrance });
  }
}

let open = false;
let drawerAnim = null;
let scrimAnim = null;
let lastFocus = null;
let hooks = { lock() {}, unlock() {}, closeOthers() {} };

export function openFigsBag() {
  if (!ui) return;
  if (open) { riseUnseen(); return; }
  hooks.closeOthers('cart');
  open = true;
  hooks.lock();
  lastFocus = document.activeElement;

  // under the promo bar while it shows, like the account and country drawers
  const promoBar = document.getElementById('fnav-promo-bar');
  const top = promoBar ? Math.max(0, promoBar.getBoundingClientRect().bottom) : 0;
  ui.drawer.style.top = `${top}px`;
  ui.scrim.style.top = `${top}px`;

  ui.drawer.inert = false;
  ui.drawer.setAttribute('aria-hidden', 'false');
  ui.scrim.style.pointerEvents = 'auto';
  ui.drawer.style.pointerEvents = 'auto';
  drawerAnim?.cancel?.();
  scrimAnim?.cancel?.();
  ui.drawer.style.opacity = '1';
  ui.drawer.style.transform = 'translateX(0%)';
  ui.scrim.style.opacity = '1';

  if (isReduced()) {
    drawerAnim = animate(ui.drawer, { opacity: [0, 1] }, { duration: 0.2, ease: DRAWER_EASE });
  } else {
    drawerAnim = animate(ui.drawer, { opacity: [0, 1], transform: ['translateX(100%)', 'translateX(0%)'] }, { duration: 0.3, ease: DRAWER_EASE });
  }
  scrimAnim = animate(ui.scrim, { opacity: [0, 1] }, { duration: 0.3, ease: DRAWER_EASE });
  riseUnseen({ delay: 0.12 }); // the panel arrives first, then the new line
  setTimeout(() => { if (open) ui.close.focus({ preventScroll: true }); }, 60);
}

export function closeFigsBag() {
  if (!ui || !open) return;
  open = false;
  hooks.unlock();
  ui.drawer.setAttribute('aria-hidden', 'true');
  ui.drawer.inert = true;
  ui.scrim.style.pointerEvents = 'none';
  ui.drawer.style.pointerEvents = 'none';
  drawerAnim?.cancel?.();
  scrimAnim?.cancel?.();
  ui.drawer.style.opacity = '0';
  ui.drawer.style.transform = 'translateX(100%)';
  ui.scrim.style.opacity = '0';
  if (isReduced()) {
    drawerAnim = animate(ui.drawer, { opacity: [1, 0] }, { duration: 0.2, ease: DRAWER_EASE });
  } else {
    drawerAnim = animate(ui.drawer, { opacity: [1, 0], transform: ['translateX(0%)', 'translateX(100%)'] }, { duration: 0.25, ease: DRAWER_EASE });
  }
  scrimAnim = animate(ui.scrim, { opacity: [1, 0] }, { duration: 0.25, ease: DRAWER_EASE });
  if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  lastFocus = null;
}

/* ---------------------------------------------------------------------------
   Adding — the one entry point every prototype's add-to-bag calls.

     items   one line or a list — a set is always one line per garment
             (a set-named item, or one with `parts`, is split here):
             { id, name, color, size, type, price, image, fallback, url, note, qty, embroidery }
             `embroidery` is the configurator's bagLine() — `price` is then
             the garment's alone; the bag adds the charges.
             `image` is the on-model photo, never a ghost; `fallback` is
             another on-model shot if that colour's photo is missing.
     from    where the product was picked (an <img>, or a list of fallbacks)
             — the flight starts there; leave it out when a drawer confirms
     open    true: the bag opens as the product lands. false: the caller
             opens it (openFigsBag) once its own drawer has gone.

   Resolves with the bag's new count once the badge has updated.
   ------------------------------------------------------------------------ */
export async function addToFigsBag(items, { from = null, open: openAfter = true } = {}) {
  const added = [].concat(items).filter(Boolean).flatMap(explode).map(normalize);
  if (!added.length) return count();
  const touched = []; // the lines this add made or raised, in the order given
  added.forEach((line) => {
    const hit = touched.find((l) => l.key === line.key) || lines.find((l) => l.key === line.key);
    if (!hit) { touched.push(line); return; }
    hit.qty += line.qty;
    if (!touched.includes(hit)) touched.push(hit);
  });
  lines = [...touched, ...lines.filter((l) => !touched.includes(l))];
  touched.forEach((l) => unseen.add(l.key));
  save();
  render();

  /* blob: images (recoloured in the page) are swapped for copies that
     survive the page; the drawer shows the blob meanwhile */
  touched.filter((l) => l.image.startsWith('blob:')).forEach((l) => {
    keepable(l.image).then((src) => {
      l.image = src;
      save();
      const img = ui && lineEl(l.key)?.querySelector('.fnav-cart-product-image img');
      if (img) img.src = src;
    }).catch(() => {});
  });

  await (from ? flyToFigsBag(from, added[0].image) : Promise.resolve());
  setFigsNavBagCount(count());
  if (openAfter) openFigsBag();
  return count();
}

export const figsBagCount = () => count();

/* ---------------------------------------------------------------------------
   Mount — called once by mountFigsNav(), which hands over its scroll lock and
   overlay coordination so the bag behaves like the nav's other drawers.
   ------------------------------------------------------------------------ */
export function mountFigsCart({ lockBodyScroll, unlockBodyScroll, closeOtherOverlays } = {}) {
  if (ui) return;
  hooks = { lock: lockBodyScroll || hooks.lock, unlock: unlockBodyScroll || hooks.unlock, closeOthers: closeOtherOverlays || hooks.closeOthers };
  const t = document.createElement('template');
  t.innerHTML = DRAWER_HTML.trim();
  document.body.append(t.content);
  const drawer = document.getElementById('fnav-cart-drawer');
  ui = {
    drawer,
    scrim: document.getElementById('fnav-cart-drawer-scrim'),
    close: document.getElementById('fnav-cart-drawer-close'),
    title: document.getElementById('fnav-cart-drawer-title'),
    empty: drawer.querySelector('[data-cart-empty]'),
    list: drawer.querySelector('[data-cart-lines]'),
    scroll: drawer.querySelector('[data-cart-scroll]'),
    bottom: drawer.querySelector('[data-cart-bottom]'),
    subtotal: drawer.querySelector('[data-cart-subtotal]'),
    shipping: drawer.querySelector('[data-cart-shipping]'),
    embBanner: drawer.querySelector('[data-cart-emb-banner]'),
  };
  render();

  wrapBagIcons().forEach((btn) => btn.addEventListener('click', (e) => {
    e.preventDefault();
    openFigsBag();
  }));
  setFigsNavBagCount(count(), { bump: false });

  ui.close.addEventListener('click', closeFigsBag);
  ui.scrim.addEventListener('click', closeFigsBag);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && open) closeFigsBag(); });
  document.addEventListener('overlay:open', (e) => { if (e.detail.except !== 'cart') closeFigsBag(); });

  ui.list.addEventListener('click', (e) => {
    const li = e.target.closest('[data-cart-line]');
    if (!li || li.dataset.removing) return;
    const line = lines.find((l) => l.key === li.dataset.cartLine);
    if (!line) return;
    const embToggle = e.target.closest('[data-cart-emb-toggle]');
    const embEdit = e.target.closest('[data-cart-emb-edit]');
    const embRemove = e.target.closest('[data-cart-emb-remove]');
    if (embToggle) { toggleEmb(li, line, embToggle.getAttribute('aria-expanded') !== 'true'); return; }
    if (embEdit) { editEmb(embEdit, line); return; }
    if (embRemove) {
      const [place, part] = embRemove.dataset.cartEmbRemove.split(':');
      removeEmbItem(li, line, place, part);
      return;
    }
    const qtyBtn = e.target.closest('[data-cart-qty]');
    if (qtyBtn) {
      const dir = Number(qtyBtn.dataset.cartQty);
      if (dir < 0 && line.qty <= 1) return; // one is the floor; Remove takes it out
      line.qty += dir;
      save();
      flipQty(li, line.qty, dir);
      syncTotals();
      setFigsNavBagCount(count(), { bump: false });
    } else if (e.target.closest('[data-cart-remove]')) {
      removeLine(li);
    }
  });

  /* another tab (or another prototype open beside this one) changed the bag */
  addEventListener('storage', (e) => {
    if (e.key !== STORE) return;
    lines = load();
    render();
    setFigsNavBagCount(count(), { bump: false });
  });
}
